import { Link } from 'react-router-dom';
import { ArrowRight, CalendarDays } from 'lucide-react';
import { getLatestArticles, formatInsightDate, topicFor } from '@/data/insights';
import { useLiveTick } from '@/lib/liveContent';
import { SectionHeader } from './SectionHeader';
import { Reveal } from './Reveal';

const formatDate = formatInsightDate;

export const Insights = () => {
  useLiveTick();
  const latest = getLatestArticles(3);

  return (
    <section aria-labelledby="insights-heading" data-testid="insights-section" className="bg-mist py-20 sm:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
          <SectionHeader
            index="07"
            eyebrow="Insights"
            title="Latest insights & updates"
            align="left"
            testid="insights-header"
          />
          <Link
            to="/insights"
            data-testid="insights-view-all"
            className="group inline-flex shrink-0 items-center gap-2 text-sm font-bold text-navy-900 transition-colors hover:text-gold-700"
          >
            View all insights
            <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true" />
          </Link>
        </div>

        <div className="mt-12 grid gap-6 md:grid-cols-3">
          {latest.map((post, i) => {
            const topic = topicFor(post.primaryTopic);
            return (
            <Reveal key={post.slug} delay={i * 0.08} className="h-full">
              <Link
                to={`/insights/${post.slug}`}
                data-testid={`insight-card-${i + 1}`}
                aria-label={`Read article: ${post.title}`}
                className="group flex h-full flex-col overflow-hidden rounded-xl border border-slate-200 bg-white transition-[box-shadow,transform] duration-300 hover:-translate-y-1 hover:shadow-lift"
              >
                {post.image?.src && (
                  <div className="overflow-hidden">
                    <img
                      src={post.image.src}
                      alt={post.image?.alt || post.title}
                      width="1080"
                      height="675"
                      loading="lazy"
                      className="aspect-[16/10] w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                )}
                <div className="flex flex-1 flex-col p-6">
                  <div className="flex items-center gap-3">
                    {topic && (
                      <span
                        className="rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wide"
                        style={{ backgroundColor: topic.soft, color: topic.dark }}
                      >
                        {topic.label}
                      </span>
                    )}
                    <time dateTime={post.publishedAt} className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500">
                      <CalendarDays className="h-3.5 w-3.5" aria-hidden="true" />
                      {formatDate(post.publishedAt)}
                    </time>
                  </div>
                  <h3 className="mt-4 text-lg font-extrabold leading-snug tracking-tight text-navy-900 transition-colors group-hover:text-navy-600">
                    {post.title}
                  </h3>
                  <p className="mt-2.5 line-clamp-3 text-sm leading-relaxed text-slate-600">{post.excerpt}</p>
                  <span className="mt-auto inline-flex items-center gap-1.5 pt-5 text-sm font-bold text-navy-900">
                    Read article
                    <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true" />
                  </span>
                </div>
              </Link>
            </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};
